import { requiresCastFramework } from "./utils.js";
import { GoogleCastProvider } from "./provider.js";
import { getMediaElement } from "@videojs/media/dom";
import { isUndefined } from "@videojs/utils/predicate";
//#region src/extension.ts
/**
* Player extension that adds Google Cast to whatever media the player attaches: a plain `<video>`, a custom media
* element, or a media adapter. While a cast session is connected, playback members the player reads route to the
* receiver; otherwise only `remote` is taken over so the cast button can prompt.
*
* Its `PlayerExtension` members are internal: the player drives them, and the element and hook that register it check
* that it conforms.
*/
var GoogleCastExtension = class {
	static {
		this.defaultProps = {
			src: void 0,
			contentType: void 0,
			streamType: void 0,
			receiver: void 0,
			customData: void 0
		};
	}
	#src;
	#contentType;
	#streamType;
	#receiver;
	#customData;
	#media = null;
	#provider = null;
	#override = null;
	#connected = false;
	constructor(props = {}) {
		Object.assign(this, props);
	}
	/** @internal Player lifecycle; the player calls it. */
	attach({ media }) {
		const target = media;
		if (this.#media === target) return;
		this.detach();
		this.#media = target;
		if (requiresCastFramework() && !this.#provider) {
			this.#provider = new GoogleCastProvider(this);
			this.#provider.remote.addEventListener("connect", this.#onStateChange);
			this.#provider.remote.addEventListener("disconnect", this.#onStateChange);
			this.#override = this.#createRemoteOverride();
		}
		this.#bindProvider();
		target.addEventListener("loadstart", this.#onLoadStart);
		this.#followSource();
	}
	/** @internal Player lifecycle; the player calls it. */
	detach() {
		this.#media?.removeEventListener("loadstart", this.#onLoadStart);
		this.#media = null;
		this.#provider?.detach();
	}
	destroy() {
		this.detach();
		this.#provider?.destroy();
		this.#provider = null;
		this.#override = null;
		this.#connected = false;
	}
	/** @internal Read by the player to route media members to the receiver. */
	get mediaOverride() {
		return this.#override;
	}
	/**
	* Point the provider at the native element behind the media: its `<track>` children carry the real modes, and events
	* dispatched there already forward through any custom element or adapter to the player's listeners. A custom element
	* or adapter can swap that element (an engine change, for example), so this runs again on every `loadstart`.
	*/
	#bindProvider() {
		const provider = this.#provider;
		const media = this.#media;
		if (!provider || !media) return;
		const element = getMediaElement(media) ?? media;
		if (provider.target === element) return;
		if (provider.target) provider.detach();
		provider.attach(element);
	}
	#onStateChange = () => {
		if (!this.#provider) return;
		this.#connected = this.#provider.remote.state === "connected";
		this.#override = this.#connected ? this.#provider : this.#createRemoteOverride();
	};
	/** The media started loading a new source locally. */
	#onLoadStart = () => {
		this.#bindProvider();
		this.#followSource();
	};
	/**
	* While casting, load the media's current source on the receiver unless it is already there. The provider claims the
	* source before it starts loading, so the several `loadstart`s one local load can produce reach the receiver once.
	*/
	#followSource() {
		const provider = this.#provider;
		if (!provider || !this.#connected) return;
		const { src } = this;
		if (!src || provider.loadedSrc === src) return;
		provider.load();
	}
	#createRemoteOverride() {
		const provider = this.#provider;
		return { get remote() {
			return provider.remote;
		} };
	}
	/**
	* Source URL loaded on the Cast receiver. Falls back to the source the media is playing: its `src`, else the
	* `<source>` child the browser selected (`currentSrc`), else the first `<source>` child before selection has run. An
	* adapter reports the URL it plays on `src`; a `blob:` `currentSrc` is a MediaSource handle a receiver can't load.
	*
	* An embed (YouTube, Vimeo) has no fallback: its `src` is a provider page, not a stream a receiver can play, so it is
	* cast only when this is set explicitly. Without a source, nothing is loaded on the receiver.
	*/
	get src() {
		if (!isUndefined(this.#src)) return this.#src;
		const media = this.#media;
		if (!media || !getMediaElement(media)) return "";
		if (media.src) return media.src;
		if (media.currentSrc && !media.currentSrc.startsWith("blob:")) return media.currentSrc;
		return media.querySelector("source")?.src ?? "";
	}
	set src(value) {
		if (this.#src === value) return;
		this.#src = value;
		this.#load();
	}
	/** MIME type of the Cast source. When unset, the receiver infers it from the URL. */
	get contentType() {
		return this.#contentType;
	}
	set contentType(value) {
		if (this.#contentType === value) return;
		this.#contentType = value;
		this.#load();
	}
	/** Stream type used on the Cast receiver. Falls back to the media's `streamType` if it exposes one. */
	get streamType() {
		return this.#streamType ?? this.#media?.streamType;
	}
	set streamType(value) {
		if (this.#streamType === value) return;
		this.#streamType = value;
		this.#load();
	}
	/** Cast receiver application ID. Read on session start; falls back to the layer's default. */
	get receiver() {
		return this.#receiver;
	}
	set receiver(value) {
		if (this.#receiver === value) return;
		this.#receiver = value;
		this.#load();
	}
	/** Custom data sent to the Cast receiver with the load request. */
	get customData() {
		return this.#customData;
	}
	set customData(value) {
		if (this.#customData === value) return;
		this.#customData = value;
		this.#load();
	}
	#load() {
		if (this.#provider?.remote.state === "connected") this.#provider.load();
	}
};
//#endregion
export { GoogleCastExtension };
